import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Content,
  ErrorPanel,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import BuildIcon from '@material-ui/icons/Build';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { mergeOpsApiRef } from '../../api/MergeOpsApi';
import { cloudBuildUrl } from '../../links';
import { PrSummary } from '../../api/types';
import { stageRank } from '../../stages';
import { PipelineStages } from './PipelineStages';
import { PrRow } from './PrRow';
import { useOwnsEntity } from '../../hooks/useOwnsEntity';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  refreshError: {
    marginBottom: theme.spacing(2),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  list: {
    backgroundColor: theme.palette.background.paper,
  },
  emptyList: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(2),
  },
  prListHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  prListTitle: { fontWeight: 600 },
  prListCount: { fontWeight: 600, minWidth: 28 },
}));

type CurrentTags = {
  dev: string | null;
  stg: string | null;
  prd: string | null;
};

/**
 * The env tag a deploy PR would replace, for the row's "current → pending"
 * hint. Build PRs and combined (env/all) PRs have no single current tag.
 */
function currentTagFor(pr: PrSummary, tags: CurrentTags): string | null {
  if (pr.prType !== 'deploy') return null;
  if (pr.env === 'stg') return tags.stg;
  if (pr.env === 'prd') return tags.prd;
  return null;
}

const Header = ({
  componentName,
  projectName,
  onRefresh,
}: {
  componentName: string;
  projectName: string;
  onRefresh: () => void;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.header}>
      <Typography variant="h5">MergeOps — {componentName}</Typography>
      <Box className={classes.headerActions}>
        <Tooltip title="Open this service's build history in Cloud Build">
          <Button
            component="a"
            href={cloudBuildUrl(projectName)}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            startIcon={<BuildIcon fontSize="small" />}
            endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
          >
            Builds
          </Button>
        </Tooltip>
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const RenderError = ({ error }: { error: Error }) => {
  if ((error as any).cause?.response || (error as any).response) {
    return <ResponseErrorPanel error={error as any} />;
  }
  return <ErrorPanel error={error} />;
};

export const EntityMergeOpsContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(mergeOpsApiRef);
  const componentName = entity.metadata.name;
  // Only members of the component's owning Group (or a descendant) may operate,
  // matching the backend's assertCanOperate; other teams' actions are disabled.
  const ownerRef = entity.spec?.owner
    ? stringifyEntityRef(
        parseEntityRef(String(entity.spec.owner), {
          defaultKind: 'group',
          defaultNamespace: 'default',
        }),
      )
    : undefined;
  const canOperate = useOwnsEntity(ownerRef);
  const [refreshTick, setRefreshTick] = useState(0);
  // Local copy so Approve/Merge can update a single PR without refetching
  // (and flashing) the whole tab.
  const [items, setItems] = useState<PrSummary[]>([]);
  const [projectName, setProjectName] = useState<string>(componentName);
  const [currentTags, setCurrentTags] = useState<{
    dev: string | null;
    stg: string | null;
    prd: string | null;
  }>({ dev: null, stg: null, prd: null });
  const [deployprManaged, setDeployprManaged] = useState(false);
  // When the list below was read from GitHub — each row shows its own age off
  // this, and re-stamps it locally when refreshed on its own.
  const [fetchedAtMs, setFetchedAtMs] = useState<number | undefined>(undefined);

  const state = useAsync(
    () => api.listPrs({ componentName }),
    [componentName, refreshTick],
  );

  useEffect(() => {
    if (state.value) {
      setItems(state.value.items);
      setProjectName(state.value.projectName);
      setCurrentTags(state.value.currentTags);
      setDeployprManaged(state.value.deployprManaged ?? false);
      setFetchedAtMs(Date.now());
    }
  }, [state.value]);

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  // After a Promote/Cherry-pick dispatch the workflow opens the PR a bit later
  // (~1 min), so poll a few times to surface it without a manual refresh.
  // Timer ids are kept so an unmount cancels the pending refreshes.
  const dispatchTimersRef = useRef<number[]>([]);
  const onDispatched = useCallback(() => {
    [12000, 30000, 50000, 75000].forEach(ms =>
      dispatchTimersRef.current.push(
        window.setTimeout(() => setRefreshTick(t => t + 1), ms),
      ),
    );
  }, []);
  useEffect(
    () => () => {
      dispatchTimersRef.current.forEach(id => window.clearTimeout(id));
    },
    [],
  );

  const onMerged = useCallback(
    (prNumber: number) =>
      setItems(prev => prev.filter(pr => pr.number !== prNumber)),
    [],
  );

  const onPrRefreshed = useCallback(
    (fresh: PrSummary) =>
      setItems(prev =>
        prev.map(pr => (pr.number === fresh.number ? fresh : pr)),
      ),
    [],
  );

  if (state.loading && !state.value) {
    return (
      <Content>
        <Progress />
      </Content>
    );
  }

  if (state.error && !state.value) {
    return (
      <Content>
        <Header
          componentName={componentName}
          projectName={projectName}
          onRefresh={refresh}
        />
        <RenderError error={state.error} />
      </Content>
    );
  }

  const openPrs = [...items].sort((a, b) => stageRank(a) - stageRank(b));

  return (
    <Content>
      <Header
        componentName={componentName}
        projectName={projectName}
        onRefresh={refresh}
      />
      {/* A failed refresh with data on screen would otherwise be silent: the
          list quietly stays at its last loaded state while looking current. */}
      {state.error && state.value && (
        <Alert severity="warning" className={classes.refreshError}>
          Refreshing failed; showing the last loaded state.{' '}
          {String(state.error.message ?? state.error)}
        </Alert>
      )}
      <PipelineStages
        componentName={componentName}
        projectName={projectName}
        currentTags={currentTags}
        onDispatched={onDispatched}
        canOperate={canOperate}
        deployprManaged={deployprManaged}
      />

      <Divider />
      <Box className={classes.prListHeader}>
        <Typography variant="h6" className={classes.prListTitle}>
          Open Pull Requests
        </Typography>
        <Chip
          size="small"
          label={openPrs.length}
          className={classes.prListCount}
        />
      </Box>

      {openPrs.length === 0 ? (
        <Typography className={classes.emptyList}>
          No open pull requests.
        </Typography>
      ) : (
        <Card variant="outlined" className={classes.list}>
          {openPrs.map((pr, i) => (
            <Fragment key={pr.number}>
              {i > 0 && <Divider />}
              <PrRow
                pr={pr}
                componentName={componentName}
                projectName={projectName}
                currentTag={currentTagFor(pr, currentTags)}
                onMerged={onMerged}
                canOperate={canOperate}
                fetchedAtMs={fetchedAtMs}
                onRefreshed={onPrRefreshed}
              />
            </Fragment>
          ))}
        </Card>
      )}
    </Content>
  );
};
